import React, { FC, useMemo, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import Typography from '@mui/material/Typography'
import { Checkbox, LoadingButton } from '../atoms'
import Tooltip from '@mui/material/Tooltip'
import { useScrollHighlight } from '../../lib/hooks/useScrollHighlight'
import { ProfileDetailsForm } from '../organisms/ProfileDetailsForm'
import { RPUser } from '@hazadapt-git/public-core-base'
import { useFormikStateSync } from '../../lib/hooks/useFormikStateSync'
import { ProfileFormMode } from '../../lib/entities'

interface SetupProfilePageTemplateProps {
    entityName: string
    user: RPUser
    onSubmit: (values: Partial<RPUser>) => Promise<void>
}

export const SetupProfilePageTemplate: FC<SetupProfilePageTemplateProps> = (
    props: SetupProfilePageTemplateProps
) => {
    const { classes: localClasses } = useLocalStyles()
    const { formikRef, formikState, FormikStateBridge } =
        useFormikStateSync<Partial<RPUser>>()
    const [termsAndConditionsRef, scrollToTermsAndConditions] =
        useScrollHighlight()
    const [checked, setChecked] = useState<boolean>(false)

    const ContinueButtonWithTooltip = useMemo(
        () => (
            <Tooltip
                title={
                    !formikState.isValid
                        ? 'Please complete all required fields to continue.'
                        : !checked
                        ? 'Please read and agree to the Terms & Conditions to continue.'
                        : ''
                }
                disableHoverListener={checked && formikState.isValid}
                disableFocusListener={checked && formikState.isValid}
                arrow
                PopperProps={{
                    onClick:
                        !checked && formikState.isValid
                            ? scrollToTermsAndConditions
                            : undefined,
                }}
            >
                {/* the span is necessary for Tooltip to appear while button is disabled */}
                <span
                    className={localClasses.buttonWrapper}
                    onClick={
                        !checked && formikState.isValid
                            ? scrollToTermsAndConditions
                            : undefined
                    }
                >
                    <LoadingButton
                        variant="contained"
                        color="primary"
                        onClickAsync={async () =>
                            await formikRef.current?.submitForm?.()
                        }
                        disabled={!checked || !formikState.isValid}
                    >
                        Continue
                    </LoadingButton>
                </span>
            </Tooltip>
        ),
        [
            checked,
            localClasses.buttonWrapper,
            formikRef,
            formikState.isValid,
            scrollToTermsAndConditions,
        ]
    )

    const CheckBoxAndContinueButton = useMemo(
        () => (
            <>
                <div
                    className={localClasses.checkboxWrapper}
                    ref={termsAndConditionsRef}
                >
                    <Checkbox
                        checked={checked}
                        onChange={(e) => setChecked(e.target.checked)}
                        label={
                            <Typography variant="body2">
                                I have read and agree to the{' '}
                                <a
                                    href="https://www.hazadapt.com/terms/hazadapt-terms-and-conditions"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Terms & Conditions
                                </a>{' '}
                                for HazAdapt Community Resilience Tools.
                            </Typography>
                        }
                    />
                </div>
                {ContinueButtonWithTooltip}
            </>
        ),
        [
            ContinueButtonWithTooltip,
            checked,
            localClasses.checkboxWrapper,
            termsAndConditionsRef,
        ]
    )

    return (
        <div className={localClasses.container}>
            <Typography variant="h4" className={localClasses.welcomeText}>
                Welcome to {props.entityName}'s ResiliencePoint space!
            </Typography>
            <Typography>First, complete your profile.</Typography>
            {ContinueButtonWithTooltip}
            <ProfileDetailsForm
                formikRef={formikRef}
                FormikStateBridge={FormikStateBridge}
                targetUser={props.user}
                onSubmit={props.onSubmit}
                mode={ProfileFormMode.SETUP_SELF}
            />
            {CheckBoxAndContinueButton}
        </div>
    )
}

const useLocalStyles = makeStyles()({
    container: {
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        padding: '2rem',
    },
    welcomeText: {
        display: 'flex',
        padding: '2rem 0 1rem',
    },
    buttonWrapper: { alignSelf: 'flex-end', zIndex: 1 },
    checkboxWrapper: {
        alignSelf: 'center',
        margin: '2rem 0',
        padding: '0 .5rem',
    },
})
